import React, { useState, useRef, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AlertBubbleIcon } from '@shopify/polaris-icons';
import { Navigation } from '@shopify/polaris';
import Grid from '@mui/material/Grid';
import { AuthenticationContext } from '../../../homeModules/auth/contexts/AuthenticationContext';
import { useCorporateLocation } from '../../../sharedModules/utils/useCorporateLocation';
import { useChillerJobApprovals } from '../../jobApprovals/hooks/useChillerJobApprovals';
import { format, addDays, isFriday } from 'date-fns';

const today = new Date();
const futureDate = addDays(today, isFriday(today) ? 3 : 1);

const ChillerLogSidebar: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { user } = useContext(AuthenticationContext);
  const selectedLocation = useCorporateLocation('chiller');

  const { data: chillerApprovalsData } = useChillerJobApprovals({
    teamId: user?.led_team,
    location: selectedLocation,
    enabled: !!user,
  });
  const approvalQueueCount = chillerApprovalsData?.data?.total_records;
  const approvalQueueBadgeDisplay = approvalQueueCount > 0 ? String(approvalQueueCount) : undefined;

  const isTechnicianUser = user?.modules?.some(
    (module) => module.value === 'chiller_logs' && parseInt(module.tier) <= 2,
  );
  const isApprover = user?.modules?.some((module) => module.value === 'chiller_logs' && module.can_review);
  const isAdminUser = user?.modules?.some((module) => module.value === 'chiller_logs' && parseInt(module.tier) > 2);

  const [rebatesToReviewCount] = useState<number>(0);
  const anchorRef = useRef<HTMLDivElement>(null);

  const hasAdminAccess = isAdminUser || isApprover;
  const navigationItems = [];

  const defaultFilterValues = {
    user_id_eq: 0,
    chiller_jobs_scheduled_date_gteq: format(today, 'yyyy-MM-dd'),
    chiller_jobs_scheduled_date_lteq: format(futureDate, 'yyyy-MM-dd'),
    status_eq: 'not_started',
    chiller_jobs_company_name_eq: 'all',
  };

  if (hasAdminAccess) {
    navigationItems.push({
      label: 'Admin Chiller Logs',
      url: '/chiller_logs/search',
      icon: AlertBubbleIcon,
      matches: true,
      subNavigationItems: [
        {
          url: '/chiller_logs/search',
          disabled: false,
          label: 'Jobs',
          onClick: () => {
            sessionStorage.setItem('chillerFilterValues', JSON.stringify(defaultFilterValues));
          },
        },
        {
          url: '/chiller_logs/approvals',
          disabled: false,
          label: 'Approval Queue',
          badge: approvalQueueBadgeDisplay,
        },
      ],
    });
  } else if (isApprover) {
    navigationItems.push({
      label: 'Technician Chiller Logs',
      url: '/chiller_logs/search',
      icon: AlertBubbleIcon,
      matches: true,
      subNavigationItems: [
        {
          url: '/chiller_logs/search',
          disabled: false,
          label: 'Jobs',
          onClick: () => {
            sessionStorage.setItem('chillerFilterValues', JSON.stringify(defaultFilterValues));
          },
        },
        {
          url: '/chiller_logs/approvals',
          disabled: false,
          label: 'Approval Queue',
          badge: approvalQueueBadgeDisplay,
        },
      ],
    });
  } else if (isTechnicianUser) {
    navigationItems.push({
      label: 'Technician Chiller Logs',
      url: '/chiller_logs/search',
      icon: AlertBubbleIcon,
      matches: true,
      subNavigationItems: [
        {
          url: '/chiller_logs/search',
          disabled: false,
          label: 'Jobs',
          onClick: () => {
            sessionStorage.setItem('chillerFilterValues', JSON.stringify(defaultFilterValues));
          },
        },
      ],
    });
  }

  if (isAdminUser) {
    navigationItems.push({
      label: 'Module Settings',
      url: '/chiller_logs/settings',
      icon: AlertBubbleIcon as any,
      matches: true,
      subNavigationItems: [
        {
          url: '/chiller_logs/settings',
          disabled: false,
          label: 'Field Validations',
          onClick: () => {
            sessionStorage.setItem('chillerFilterValues', JSON.stringify(defaultFilterValues));
          },
        },
        {
          url: '/chiller_logs/servman_sql_logs',
          disabled: false,
          label: 'Servman SQL',
        },
      ],
    });
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Navigation location={location.pathname}>
          <Navigation.Section items={navigationItems} />
        </Navigation>
      </Grid>
    </Grid>
  );
};

export default ChillerLogSidebar;
